import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Clock, Disc } from "lucide-react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserEventsService } from "../services/UserEventsServices";

export default function Events() {
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('upcoming');

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const data = await UserEventsService.getAllEvents();
            setUpcomingEvents(data);
        } catch (err) {
            console.error("Failed to fetch events:", err);
            setError("Unable to load tour dates at this time. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const filteredEvents = (upcomingEvents || [])
        .filter(event => activeTab === 'upcoming' ? !event.is_past : event.is_past);

    const displayEvents = filteredEvents;

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="border border-black/10 bg-white p-8 animate-pulse shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                        <div className="md:col-span-2 space-y-3">
                            <div className="h-12 w-16 bg-gray-200 rounded"></div>
                            <div className="h-4 w-12 bg-gray-200 rounded"></div>
                        </div>
                        <div className="md:col-span-7 space-y-4">
                            <div className="h-8 w-64 bg-gray-200 rounded"></div>
                            <div className="flex gap-4">
                                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                            </div>
                            <div className="h-4 w-full bg-gray-200 rounded"></div>
                        </div>
                        <div className="md:col-span-3 flex md:justify-end items-center">
                            <div className="h-12 w-32 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-white text-black">
            <SEO title="Tour Dates & Live Performances" description="Find upcoming live performances, concert dates, and tour tickets for Temidayo Balogun." url="/events" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6">

                    {/* Hero / Title Section */}
                    <div className="mb-12 sm:mb-16 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Tour Dates
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />

                        {/* Tab Toggle */}
                        <div className="flex justify-center gap-8 sm:gap-12 mt-12 border-b border-black/5">
                            <button
                                onClick={() => setActiveTab('upcoming')}
                                className={`pb-4 text-xs sm:text-sm font-bold tracking-[3px] uppercase transition-all relative ${activeTab === 'upcoming' ? 'text-black' : 'text-black/30 hover:text-black/60'
                                    }`}
                            >
                                Upcoming
                                {activeTab === 'upcoming' && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-jazz-brown" />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('past')}
                                className={`pb-4 text-xs sm:text-sm font-bold tracking-[3px] uppercase transition-all relative ${activeTab === 'past' ? 'text-black' : 'text-black/30 hover:text-black/60'
                                    }`}
                            >
                                Past Events
                                {activeTab === 'past' && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-jazz-brown" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Events List */}
                    <div className="mb-24 min-h-[400px]">
                        {loading ? (
                            <LoadingSkeleton />
                        ) : error ? (
                            <div className="text-center py-20 border border-dashed border-black/10">
                                <p className="text-black font-serif italic">{error}</p>
                            </div>
                        ) : displayEvents.length > 0 ? (
                            <div className="space-y-6">
                                {displayEvents.map((event, index) => (
                                    <article
                                        key={event.id || index}
                                        className="group border border-black/10 bg-white hover:border-black hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 transform hover:-translate-y-1"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 lg:p-8">

                                            {/* Date Column */}
                                            <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-2 border-b md:border-b-0 md:border-r border-black/10 pb-4 md:pb-0 md:pr-6">
                                                <span className="text-5xl sm:text-6xl font-bold text-jazz-brown leading-none" style={{ fontFamily: "Playfair Display, serif" }}>
                                                    {event.day}
                                                </span>
                                                <div className="flex flex-row md:flex-col gap-2 md:gap-0 items-baseline md:items-start">
                                                    <span className="text-sm font-bold tracking-widest uppercase text-black">{event.month}</span>
                                                    <span className="text-sm text-black/90 font-medium">{event.year}</span>
                                                </div>
                                            </div>

                                            {/* Info Column */}
                                            <div className="md:col-span-7 flex flex-col justify-center space-y-3">
                                                <h3
                                                    className="text-2xl sm:text-3xl font-medium text-black"
                                                    style={{ fontFamily: "Playfair Display, serif" }}
                                                >
                                                    {event.title}
                                                </h3>

                                                <div className="flex flex-wrap gap-4 text-sm text-black/90 font-semibold">
                                                    <div className="flex items-center gap-1.5">
                                                        <MapPin size={16} className="text-jazz-brown" />
                                                        <span>{event.location}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <MapPin size={16} className="text-jazz-brown" />
                                                        <span>{event.venue}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <Clock size={16} className="text-jazz-brown" />
                                                        <span>{event.time}</span>
                                                    </div>
                                                </div>

                                                <p className="text-black text-sm leading-relaxed max-w-2xl font-serif">
                                                    {event.description}
                                                </p>
                                            </div>

                                            {/* Action Column */}
                                            <div className="md:col-span-3 flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-4 border-t md:border-t-0 md:border-l border-black/10 pt-4 md:pt-0 md:pl-6">
                                                <span className={`px-3 py-1 text-xs font-bold tracking-wider uppercase border ${event.status === 'Sold Out' ? 'bg-black text-white border-black' :
                                                    event.status === 'Presale' ? 'text-jazz-brown border-jazz-brown' :
                                                        event.is_past ? 'bg-gray-100 text-black/40 border-black/5' :
                                                            'text-black border-black/20'
                                                    }`}>
                                                    {event.is_past ? 'Completed' : event.status}
                                                </span>

                                                {!event.is_past && event.status === 'On Sale' && event.ticket_url && (
                                                    <a
                                                        href={event.ticket_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="group/btn inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm font-bold tracking-wider hover:bg-jazz-brown transition-colors duration-300"
                                                    >
                                                        TICKETS
                                                        <ArrowRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                                                    </a>
                                                )}
                                            </div>

                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 border border-dashed border-black/20 bg-gray-50/50">
                                <Disc size={48} className="mx-auto text-black/60 mb-4" />
                                <p className="text-black font-serif italic uppercase tracking-widest text-sm">
                                    No {activeTab} events at the moment.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Booking Contact Section */}
                    <div className="bg-jazz-brown/5 border border-jazz-brown/20 p-8 sm:p-12 text-center max-w-3xl mx-auto">
                        <h3
                            className="text-2xl font-medium text-black mb-4"
                            style={{ fontFamily: "Playfair Display, serif" }}
                        >
                            Booking Inquiries
                        </h3>
                        <p className="text-black mb-6 font-serif">
                            For booking, masterclasses, and private events, please contact management.
                        </p>
                        <Link
                            to="mailto:booking@temidayobalogun.com"
                            className="inline-block border-b-2 border-jazz-brown text-jazz-brown font-bold hover:text-black hover:border-black transition-colors duration-300 pb-1"
                        >
                            booking@temidayobalogun.com
                        </Link>
                    </div>

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
